import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export type GaitSpeedWalkingAid = 'none' | 'cane' | 'walker' | 'other';

export interface GaitSpeedProtocol {
  distanceMeters: number;
  pace: 'usual';
  staticStart: boolean;
  stopwatchStartsOnMovement: boolean;
  stopwatchStopsOnFirstFootPastCone: boolean;
  customaryAidAllowed: boolean;
  physicalAssistanceAllowed: boolean;
}

export interface GaitSpeedAttemptPayload {
  completed: boolean;
  timeSeconds: number | null;
  speedMs: number | null;
  walkingAid: GaitSpeedWalkingAid;
  walkingAidLabel: string;
  customAidDescription?: string;
  physicalAssistance: boolean;
  oxygenUsed: boolean;
  reasonNotCompleted?: string;
  observations?: string;
  finishedAt?: string;
}

export interface GaitSpeedReferenceComparison {
  source: 'Furlanetto2022';
  predictedSpeedMs: number;
  lowerLimitSpeedMs: number;
  normalLimitPercent: number;
  percentPredicted: number | null;
  ageYears: number;
  bmiKgM2: number;
  withinNormalLimit: boolean | null;
}

export interface GaitSpeedClinicalFlag {
  key: 'slow_gait' | 'below_reference';
  label: string;
  thresholdLabel: string;
  description: string;
  active: boolean;
}

export interface GaitSpeedResultPayload {
  attempts?: GaitSpeedAttemptPayload[];
  completed?: boolean;
  timeSeconds?: number | null;
  speedMs?: number | null;
  bestAttempt?: number | null;
  walkingAid?: GaitSpeedWalkingAid;
  walkingAidLabel?: string;
  customAidDescription?: string;
  physicalAssistance?: boolean;
  oxygenUsed?: boolean;
  reasonNotCompleted?: string;
  observations?: string;
  interpretation?: string;
  classification?: string;
  referenceComparison?: GaitSpeedReferenceComparison | null;
  clinicalFlags?: GaitSpeedClinicalFlag[];
  referenceNote?: string;
  finishedAt?: string;
  protocol?: GaitSpeedProtocol;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
}

export interface GaitSpeedSummary {
  attemptCount: number;
  bestAttempt: number | null;
  bestAttemptPayload: GaitSpeedAttemptPayload | null;
  bestTimeSeconds: number | null;
  bestSpeedMs: number | null;
  completed: boolean;
  walkingAidLabel: string;
  physicalAssistance: boolean;
  oxygenUsed: boolean;
  comparableToReference: boolean;
  referenceComparison: GaitSpeedReferenceComparison | null;
  referenceStatus: string;
  clinicalFlags: GaitSpeedClinicalFlag[];
  severity: string;
  severityRange: string;
  interpretation: string;
  referenceNote: string;
}

export const GAIT_SPEED_STANDARD_PROTOCOL: GaitSpeedProtocol = {
  distanceMeters: 4,
  pace: 'usual',
  staticStart: true,
  stopwatchStartsOnMovement: true,
  stopwatchStopsOnFirstFootPastCone: true,
  customaryAidAllowed: true,
  physicalAssistanceAllowed: false,
};

export const GAIT_SPEED_WALKING_AID_OPTIONS: Array<{ value: GaitSpeedWalkingAid; label: string; description: string }> = [
  { value: 'none', label: 'Sem dispositivo', description: 'Paciente caminhou sem auxílio de marcha.' },
  { value: 'cane', label: 'Bengala', description: 'Paciente utilizou bengala habitual.' },
  { value: 'walker', label: 'Andador', description: 'Paciente utilizou andador habitual.' },
  { value: 'other', label: 'Outro', description: 'Registrar outro recurso utilizado no teste.' },
];

export const GAIT_SPEED_DISTANCE_METERS = 4;
export const GAIT_SPEED_SLOW_THRESHOLD_MS = 0.8;
export const GAIT_SPEED_REFERENCE_NORMAL_LIMIT_PERCENT = 78;
export const GAIT_SPEED_MCID_RANGE_MS = '0,08 a 0,11 m/s';
export const GAIT_SPEED_BAR_MAX_MS = 1.6;
export const GAIT_SPEED_BAR_SCALE_TICKS = [
  { speedMs: 0, label: '0', position: 0, color: '#f43f5e' },
  { speedMs: 0.8, label: '0,8', position: 0.5, color: '#fb923c' },
  { speedMs: 1, label: '1,0', position: 0.625, color: '#facc15' },
  { speedMs: 1.2, label: '1,2', position: 0.75, color: '#22c55e' },
  {
    speedMs: GAIT_SPEED_BAR_MAX_MS,
    label: '1,6+ m/s',
    position: 1,
    color: '#10b981',
  },
] as const;
export const GAIT_SPEED_BAR_GRADIENT_CSS = `linear-gradient(90deg, ${GAIT_SPEED_BAR_SCALE_TICKS.map(
  (tick) => `${tick.color} ${tick.position * 100}%`,
).join(', ')})`;

export const GAIT_SPEED_REFERENCE_NOTE =
  'Kon et al. demonstraram que o 4MGS é confiável em DPOC, correlaciona-se com capacidade de exercício, dispneia e qualidade de vida, e classificaram velocidade lenta como < 0,8 m/s. Em estudo posterior, Kon et al. estimaram MCID de 0,08 a 0,11 m/s. Furlanetto et al. propuseram equação brasileira para adultos de 20 a 80 anos no 4MGS usual, com limite inferior de normalidade em 78% do previsto.';

type GaitSpeedBandKey = 'not_completed' | 'not_comparable' | 'preserved' | 'below_reference' | 'slow';

type GaitSpeedBand = {
  key: GaitSpeedBandKey;
  label: string;
  rangeLabel: string;
  tone: string;
  scoreColor: string;
};

const GAIT_SPEED_BANDS: Record<GaitSpeedBandKey, GaitSpeedBand> = {
  not_completed: {
    key: 'not_completed',
    label: 'Teste não concluído',
    rangeLabel: 'Sem velocidade válida',
    tone: 'bg-slate-100 text-slate-700',
    scoreColor: '#475569',
  },
  not_comparable: {
    key: 'not_comparable',
    label: 'Interpretação com cautela',
    rangeLabel: 'Assistência física registrada',
    tone: 'bg-amber-100 text-amber-700',
    scoreColor: '#d97706',
  },
  preserved: {
    key: 'preserved',
    label: 'Velocidade preservada',
    rangeLabel: '≥ 0,8 m/s e ≥ 78% do previsto, quando calculável',
    tone: 'bg-emerald-100 text-emerald-700',
    scoreColor: '#10b981',
  },
  below_reference: {
    key: 'below_reference',
    label: 'Abaixo do previsto',
    rangeLabel: '< 78% do previsto',
    tone: 'bg-amber-100 text-amber-700',
    scoreColor: '#f59e0b',
  },
  slow: {
    key: 'slow',
    label: 'Velocidade lenta',
    rangeLabel: '< 0,8 m/s',
    tone: 'bg-rose-100 text-rose-700',
    scoreColor: '#f43f5e',
  },
};

const readNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const readFiniteNumber = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseFloat(value.replace(',', '.'))
        : Number.parseFloat(String(value ?? ''));

  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeGaitSpeedTime = (value: unknown): number | null => {
  const parsed = readFiniteNumber(value);
  if (parsed == null || parsed <= 0) return null;
  return Number(parsed.toFixed(2));
};

export const normalizeGaitSpeedValue = (value: unknown): number | null => {
  const parsed = readFiniteNumber(value);
  if (parsed == null || parsed <= 0) return null;
  return Number(parsed.toFixed(2));
};

export const calculateGaitSpeedFromTime = (
  timeSeconds?: number | null,
  distanceMeters = GAIT_SPEED_DISTANCE_METERS,
): number | null => {
  if (typeof timeSeconds !== 'number' || Number.isNaN(timeSeconds) || timeSeconds <= 0) return null;
  return Number((distanceMeters / timeSeconds).toFixed(2));
};

export const calculateGaitSpeedTimeFromSpeed = (
  speedMs?: number | null,
  distanceMeters = GAIT_SPEED_DISTANCE_METERS,
): number | null => {
  if (typeof speedMs !== 'number' || Number.isNaN(speedMs) || speedMs <= 0) return null;
  return Number((distanceMeters / speedMs).toFixed(2));
};

export const formatGaitSpeed = (value?: number | null, decimals = 2): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return Number(value.toFixed(decimals)).toString().replace('.', ',');
};

export const formatGaitSpeedSeconds = (value?: number | null, decimals = 2): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return Number(value.toFixed(decimals)).toString().replace('.', ',');
};

export const formatGaitSpeedPercent = (value?: number | null, decimals = 0): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${Number(value.toFixed(decimals)).toString().replace('.', ',')}%`;
};

export const getGaitSpeedWalkingAidLabel = (
  aid?: GaitSpeedWalkingAid | null,
  customAidDescription?: string | null,
): string => {
  if (aid === 'other') {
    const custom = customAidDescription?.trim();
    return custom && custom.length > 0 ? custom : 'Outro dispositivo';
  }

  return GAIT_SPEED_WALKING_AID_OPTIONS.find((option) => option.value === aid)?.label ?? 'Sem informação';
};

const resolveGaitSpeedWalkingAid = (value: unknown): GaitSpeedWalkingAid => {
  if (value === 'none' || value === 'cane' || value === 'walker' || value === 'other') return value;
  return 'none';
};

const normalizeGaitSpeedProtocol = (value: unknown): GaitSpeedProtocol => {
  if (!value || typeof value !== 'object') return { ...GAIT_SPEED_STANDARD_PROTOCOL };

  const source = value as Partial<GaitSpeedProtocol>;
  return {
    distanceMeters:
      typeof source.distanceMeters === 'number' && source.distanceMeters > 0
        ? source.distanceMeters
        : GAIT_SPEED_STANDARD_PROTOCOL.distanceMeters,
    pace: 'usual',
    staticStart: source.staticStart !== false,
    stopwatchStartsOnMovement: source.stopwatchStartsOnMovement !== false,
    stopwatchStopsOnFirstFootPastCone: source.stopwatchStopsOnFirstFootPastCone !== false,
    customaryAidAllowed: source.customaryAidAllowed !== false,
    physicalAssistanceAllowed: Boolean(source.physicalAssistanceAllowed),
  };
};

const normalizeGaitSpeedAttempt = (value: unknown, distanceMeters = GAIT_SPEED_DISTANCE_METERS): GaitSpeedAttemptPayload | null => {
  if (!value || typeof value !== 'object') return null;

  const source = value as Record<string, unknown>;
  const completed = source.completed !== false;
  const timeSeconds = completed ? normalizeGaitSpeedTime(source.timeSeconds) : null;
  const rawSpeed = normalizeGaitSpeedValue(source.speedMs);
  const speedMs = completed ? calculateGaitSpeedFromTime(timeSeconds, distanceMeters) ?? rawSpeed : null;
  const walkingAid = resolveGaitSpeedWalkingAid(source.walkingAid);
  const customAidDescription = readNonEmptyString(source.customAidDescription);
  const walkingAidLabel = getGaitSpeedWalkingAidLabel(
    walkingAid,
    customAidDescription ?? readNonEmptyString(source.walkingAidLabel) ?? null,
  );
  const physicalAssistance = Boolean(source.physicalAssistance);
  const oxygenUsed = Boolean(source.oxygenUsed);
  const reasonNotCompleted = readNonEmptyString(source.reasonNotCompleted);
  const observations = readNonEmptyString(source.observations);
  const finishedAt = readNonEmptyString(source.finishedAt);
  const hasData =
    'completed' in source ||
    source.timeSeconds != null ||
    source.speedMs != null ||
    'walkingAid' in source ||
    'physicalAssistance' in source ||
    'oxygenUsed' in source ||
    reasonNotCompleted != null ||
    observations != null ||
    finishedAt != null;

  if (!hasData) return null;

  return {
    completed,
    timeSeconds,
    speedMs,
    walkingAid,
    walkingAidLabel,
    customAidDescription: walkingAid === 'other' ? customAidDescription : undefined,
    physicalAssistance,
    oxygenUsed,
    reasonNotCompleted: completed ? undefined : reasonNotCompleted,
    observations,
    finishedAt,
  };
};

export const calculateGaitSpeedReferenceComparison = (
  speedMs?: number | null,
  patientContext?: Record<string, unknown> | null,
): GaitSpeedReferenceComparison | null => {
  if (!patientContext || typeof patientContext !== 'object') return null;

  const ageYears = readFiniteNumber(patientContext.ageYears ?? patientContext.age);
  const heightCm = readFiniteNumber(patientContext.heightCm ?? patientContext.height);
  const explicitBmi = readFiniteNumber(patientContext.bmiKgM2 ?? patientContext.bmi);
  const weightKg = readFiniteNumber(patientContext.weightKg ?? patientContext.weight);
  const bmiKgM2 =
    explicitBmi ??
    (weightKg != null && heightCm != null && heightCm > 0 ? weightKg / Math.pow(heightCm / 100, 2) : null);

  if (ageYears == null || bmiKgM2 == null || ageYears < 20 || ageYears > 80 || bmiKgM2 <= 0) {
    return null;
  }

  const predictedSpeedMs = Number((1.466 - ageYears * 0.004 - bmiKgM2 * 0.008).toFixed(2));
  if (predictedSpeedMs <= 0) return null;

  const lowerLimitSpeedMs = Number(((predictedSpeedMs * GAIT_SPEED_REFERENCE_NORMAL_LIMIT_PERCENT) / 100).toFixed(2));
  const normalizedSpeed = normalizeGaitSpeedValue(speedMs);
  const percentPredicted =
    normalizedSpeed != null ? Number(((normalizedSpeed / predictedSpeedMs) * 100).toFixed(0)) : null;

  return {
    source: 'Furlanetto2022',
    predictedSpeedMs,
    lowerLimitSpeedMs,
    normalLimitPercent: GAIT_SPEED_REFERENCE_NORMAL_LIMIT_PERCENT,
    percentPredicted,
    ageYears: Number(ageYears.toFixed(0)),
    bmiKgM2: Number(bmiKgM2.toFixed(1)),
    withinNormalLimit: normalizedSpeed != null ? normalizedSpeed >= lowerLimitSpeedMs : null,
  };
};

export const getGaitSpeedReferenceComparisonLabel = (
  comparison?: GaitSpeedReferenceComparison | null,
): string => {
  if (!comparison || comparison.withinNormalLimit == null) return 'Referência não calculada';
  return comparison.withinNormalLimit ? 'Dentro do previsto' : 'Abaixo do previsto';
};

export const getGaitSpeedReferenceComparisonNote = (
  comparison?: GaitSpeedReferenceComparison | null,
): string => {
  if (!comparison) {
    return 'Valor previsto não calculado: requer idade de 20 a 80 anos e IMC/peso registrados.';
  }

  const percentText =
    comparison.percentPredicted != null ? `; resultado ${formatGaitSpeedPercent(comparison.percentPredicted)} do previsto` : '';
  return `Previsto ${formatGaitSpeed(comparison.predictedSpeedMs)} m/s; limite inferior ${formatGaitSpeed(
    comparison.lowerLimitSpeedMs,
  )} m/s${percentText}.`;
};

export const getGaitSpeedAttemptEntries = (payload: unknown): Array<{ index: number; attempt: GaitSpeedAttemptPayload }> => {
  if (!payload || typeof payload !== 'object') return [];

  const source = payload as GaitSpeedResultPayload;
  const protocol = normalizeGaitSpeedProtocol(source.protocol);
  const attempts = Array.isArray(source.attempts)
    ? source.attempts
        .map((attempt, index) => {
          const normalized = normalizeGaitSpeedAttempt(attempt, protocol.distanceMeters);
          return normalized ? { index: index + 1, attempt: normalized } : null;
        })
        .filter((entry): entry is { index: number; attempt: GaitSpeedAttemptPayload } => entry != null)
    : [];

  if (attempts.length) return attempts;

  const legacyAttempt = normalizeGaitSpeedAttempt(source, protocol.distanceMeters);
  return legacyAttempt ? [{ index: 1, attempt: legacyAttempt }] : [];
};

export const getGaitSpeedBestAttempt = (
  payload: unknown,
): { attempt: GaitSpeedAttemptPayload | null; index: number | null } => {
  const entries = getGaitSpeedAttemptEntries(payload);
  if (!entries.length) return { attempt: null, index: null };

  const timedEntries = entries.filter(
    (entry): entry is { index: number; attempt: GaitSpeedAttemptPayload & { speedMs: number } } =>
      entry.attempt.completed !== false && typeof entry.attempt.speedMs === 'number',
  );

  if (timedEntries.length) {
    return timedEntries.reduce((best, entry) => (entry.attempt.speedMs > best.attempt.speedMs ? entry : best));
  }

  return entries[entries.length - 1] ?? { attempt: null, index: null };
};

const resolveGaitSpeedBandKey = (
  speedMs?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: GaitSpeedReferenceComparison | null,
): GaitSpeedBandKey => {
  if (!completed || speedMs == null) return 'not_completed';
  if (physicalAssistance) return 'not_comparable';
  if (speedMs < GAIT_SPEED_SLOW_THRESHOLD_MS) return 'slow';
  if (referenceComparison?.withinNormalLimit === false) return 'below_reference';
  return 'preserved';
};

export const getGaitSpeedSeverityLabel = (
  speedMs?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: GaitSpeedReferenceComparison | null,
): string => GAIT_SPEED_BANDS[resolveGaitSpeedBandKey(speedMs, completed, physicalAssistance, referenceComparison)].label;

export const getGaitSpeedSeverityRangeLabel = (
  speedMs?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: GaitSpeedReferenceComparison | null,
): string => GAIT_SPEED_BANDS[resolveGaitSpeedBandKey(speedMs, completed, physicalAssistance, referenceComparison)].rangeLabel;

export const getGaitSpeedSeverityTone = (
  speedMs?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: GaitSpeedReferenceComparison | null,
): string => GAIT_SPEED_BANDS[resolveGaitSpeedBandKey(speedMs, completed, physicalAssistance, referenceComparison)].tone;

export const getGaitSpeedScoreColor = (
  speedMs?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: GaitSpeedReferenceComparison | null,
): string => GAIT_SPEED_BANDS[resolveGaitSpeedBandKey(speedMs, completed, physicalAssistance, referenceComparison)].scoreColor;

export const getGaitSpeedClinicalFlags = (
  speedMs?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: GaitSpeedReferenceComparison | null,
): GaitSpeedClinicalFlag[] => {
  const comparableTimedResult = completed && speedMs != null && !physicalAssistance;

  return [
    {
      key: 'slow_gait',
      label: 'Velocidade lenta',
      thresholdLabel: '< 0,8 m/s',
      description: 'Ponto de corte usado por Kon et al. para classificar marcha lenta em DPOC.',
      active: Boolean(comparableTimedResult && speedMs < GAIT_SPEED_SLOW_THRESHOLD_MS),
    },
    {
      key: 'below_reference',
      label: 'Abaixo do previsto',
      thresholdLabel: '< 78% do previsto',
      description: 'Resultado abaixo do limite inferior calculado pela equação brasileira de Furlanetto et al.',
      active: Boolean(comparableTimedResult && referenceComparison?.withinNormalLimit === false),
    },
  ];
};

export const isGaitSpeedComparableToReference = (
  payload?: Pick<GaitSpeedAttemptPayload, 'completed' | 'speedMs' | 'physicalAssistance'> | null,
): boolean => {
  if (!payload) return false;
  return Boolean(payload.completed !== false && typeof payload.speedMs === 'number' && !payload.physicalAssistance);
};

export const getGaitSpeedInterpretationText = (
  payload?: Pick<
    GaitSpeedAttemptPayload,
    'completed' | 'timeSeconds' | 'speedMs' | 'walkingAidLabel' | 'physicalAssistance' | 'oxygenUsed' | 'reasonNotCompleted'
  > & { attemptCount?: number | null; referenceComparison?: GaitSpeedReferenceComparison | null } | null,
): string => {
  const attemptCount =
    typeof payload?.attemptCount === 'number' && Number.isFinite(payload.attemptCount) && payload.attemptCount > 1
      ? Math.round(payload.attemptCount)
      : 1;
  const multiAttemptNote =
    attemptCount > 1 ? ` O melhor desempenho entre ${attemptCount} tentativas registradas foi utilizado para a interpretação.` : '';

  if (!payload || payload.completed === false || payload.speedMs == null) {
    const reason =
      typeof payload?.reasonNotCompleted === 'string' && payload.reasonNotCompleted.trim().length > 0
        ? ` Motivo registrado: ${payload.reasonNotCompleted.trim()}.`
        : '';
    return `O paciente não concluiu o teste de velocidade de marcha em 4 metros, sugerindo limitação funcional relevante para marcha curta.${reason}`;
  }

  const speedMs = normalizeGaitSpeedValue(payload.speedMs);
  const timeSeconds = normalizeGaitSpeedTime(payload.timeSeconds);
  const aidLabel =
    typeof payload.walkingAidLabel === 'string' && payload.walkingAidLabel.trim().length > 0
      ? payload.walkingAidLabel.trim()
      : 'sem informação';
  const oxygenNote = payload.oxygenUsed ? ' O uso de oxigênio suplementar foi registrado durante o teste.' : '';
  const referenceNote = payload.referenceComparison ? ` ${getGaitSpeedReferenceComparisonNote(payload.referenceComparison)}` : '';

  if (payload.physicalAssistance) {
    return `O teste foi concluído em ${formatGaitSpeedSeconds(timeSeconds)} s (${formatGaitSpeed(
      speedMs,
    )} m/s) com uso de ${aidLabel} e necessidade de assistência física. O resultado deve ser interpretado com cautela, pois não é diretamente comparável aos pontos de corte do 4MGS.${referenceNote}${oxygenNote}${multiAttemptNote}`;
  }

  if (speedMs != null && speedMs < GAIT_SPEED_SLOW_THRESHOLD_MS) {
    return `Velocidade de ${formatGaitSpeed(speedMs)} m/s em ${formatGaitSpeedSeconds(
      timeSeconds,
    )} s, abaixo do ponto de corte de 0,8 m/s usado em DPOC para classificar marcha lenta. O achado sugere pior capacidade funcional e deve ser contextualizado com dispneia, equilíbrio, quedas e testes de exercício.${referenceNote}${oxygenNote}${multiAttemptNote}`;
  }

  if (payload.referenceComparison?.withinNormalLimit === false) {
    return `Velocidade de ${formatGaitSpeed(speedMs)} m/s em ${formatGaitSpeedSeconds(
      timeSeconds,
    )} s, acima do ponto de corte de 0,8 m/s, porém abaixo do limite previsto pela equação de referência. Mudanças entre avaliações de aproximadamente ${GAIT_SPEED_MCID_RANGE_MS} podem ser clinicamente relevantes em DPOC.${referenceNote}${oxygenNote}${multiAttemptNote}`;
  }

  return `Velocidade de ${formatGaitSpeed(speedMs)} m/s em ${formatGaitSpeedSeconds(
    timeSeconds,
  )} s, compatível com velocidade de marcha preservada para o ponto de corte de 0,8 m/s e, quando a referência pôde ser calculada, dentro do limite inferior esperado. Mudanças entre avaliações de aproximadamente ${GAIT_SPEED_MCID_RANGE_MS} podem ser clinicamente relevantes em DPOC.${referenceNote}${oxygenNote}${multiAttemptNote}`;
};

export const normalizeGaitSpeedPayload = (payload: unknown): GaitSpeedResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;

  const source = payload as GaitSpeedResultPayload;
  const protocol = normalizeGaitSpeedProtocol(source.protocol);
  const attempts = getGaitSpeedAttemptEntries(source).map((entry) => entry.attempt);
  const best = getGaitSpeedBestAttempt({ ...source, attempts, protocol });
  const bestAttempt = best.attempt ?? attempts[0] ?? null;
  const attemptCount = attempts.length || (bestAttempt ? 1 : 0);
  const referenceNote = readNonEmptyString(source.referenceNote) ?? GAIT_SPEED_REFERENCE_NOTE;
  const completed = bestAttempt ? bestAttempt.completed !== false : source.completed !== false && (source.timeSeconds != null || source.speedMs != null);
  const timeSeconds =
    bestAttempt?.timeSeconds ??
    normalizeGaitSpeedTime(source.timeSeconds) ??
    calculateGaitSpeedTimeFromSpeed(normalizeGaitSpeedValue(source.speedMs), protocol.distanceMeters);
  const speedMs =
    bestAttempt?.speedMs ??
    normalizeGaitSpeedValue(source.speedMs) ??
    calculateGaitSpeedFromTime(timeSeconds, protocol.distanceMeters);
  const walkingAid = bestAttempt?.walkingAid ?? resolveGaitSpeedWalkingAid(source.walkingAid);
  const customAidDescription = bestAttempt?.customAidDescription ?? readNonEmptyString(source.customAidDescription);
  const walkingAidLabel =
    bestAttempt?.walkingAidLabel ?? getGaitSpeedWalkingAidLabel(walkingAid, customAidDescription ?? readNonEmptyString(source.walkingAidLabel) ?? null);
  const physicalAssistance = bestAttempt?.physicalAssistance ?? Boolean(source.physicalAssistance);
  const oxygenUsed = bestAttempt?.oxygenUsed ?? Boolean(source.oxygenUsed);
  const reasonNotCompleted = bestAttempt?.reasonNotCompleted ?? readNonEmptyString(source.reasonNotCompleted);
  const observations = readNonEmptyString(source.observations) ?? bestAttempt?.observations;
  const finishedAt = readNonEmptyString(source.finishedAt) ?? bestAttempt?.finishedAt;
  const referenceComparison =
    calculateGaitSpeedReferenceComparison(speedMs, source.patientContext) ??
    (source.referenceComparison && typeof source.referenceComparison === 'object'
      ? (source.referenceComparison as GaitSpeedReferenceComparison)
      : null);
  const clinicalFlags = getGaitSpeedClinicalFlags(speedMs, completed, physicalAssistance, referenceComparison);
  const interpretation = resolveAssessmentInterpretationText(
    source,
    getGaitSpeedInterpretationText({
      completed,
      timeSeconds,
      speedMs,
      walkingAidLabel,
      physicalAssistance,
      oxygenUsed,
      reasonNotCompleted,
      attemptCount,
      referenceComparison,
    }),
  );
  const classification =
    readNonEmptyString(source.classification) ??
    getGaitSpeedSeverityLabel(speedMs, completed, physicalAssistance, referenceComparison);

  return {
    attempts,
    completed,
    timeSeconds: completed ? timeSeconds : null,
    speedMs: completed ? speedMs : null,
    bestAttempt: best.index,
    walkingAid,
    walkingAidLabel,
    customAidDescription: walkingAid === 'other' ? customAidDescription : undefined,
    physicalAssistance,
    oxygenUsed,
    reasonNotCompleted: completed ? undefined : reasonNotCompleted,
    observations,
    interpretation,
    classification,
    referenceComparison,
    clinicalFlags,
    referenceNote,
    finishedAt,
    protocol,
    ...(source.patientContext ? { patientContext: source.patientContext } : {}),
    ...(source.professionalContext ? { professionalContext: source.professionalContext } : {}),
  };
};

export const buildGaitSpeedPayload = (
  attempts: GaitSpeedAttemptPayload[],
  options?: {
    referenceNote?: string;
    protocol?: Partial<GaitSpeedProtocol>;
    patientContext?: Record<string, unknown>;
    professionalContext?: Record<string, unknown>;
    observations?: string;
  },
): GaitSpeedResultPayload => {
  const basePayload: GaitSpeedResultPayload = {
    attempts,
    observations: readNonEmptyString(options?.observations),
    referenceNote: options?.referenceNote ?? GAIT_SPEED_REFERENCE_NOTE,
    protocol: {
      ...GAIT_SPEED_STANDARD_PROTOCOL,
      ...(options?.protocol ?? {}),
    },
    finishedAt: new Date().toISOString(),
    ...(options?.patientContext ? { patientContext: options.patientContext } : {}),
    ...(options?.professionalContext ? { professionalContext: options.professionalContext } : {}),
  };

  return normalizeGaitSpeedPayload(basePayload) ?? basePayload;
};

export const isGaitSpeedCompleted = (payload: unknown): boolean => {
  const normalized = normalizeGaitSpeedPayload(payload);
  return Boolean(normalized?.completed && normalized.speedMs != null);
};

export const summarizeGaitSpeedResult = (payload: unknown): GaitSpeedSummary => {
  const normalized = normalizeGaitSpeedPayload(payload);
  const best = getGaitSpeedBestAttempt(normalized ?? payload);
  const bestAttempt = best.attempt;
  const attemptCount = normalized?.attempts?.length ?? (bestAttempt ? 1 : 0);
  const completed = bestAttempt ? bestAttempt.completed !== false : Boolean(normalized?.completed);
  const bestTimeSeconds = bestAttempt?.timeSeconds ?? normalized?.timeSeconds ?? null;
  const bestSpeedMs = bestAttempt?.speedMs ?? normalized?.speedMs ?? null;
  const walkingAidLabel = bestAttempt?.walkingAidLabel ?? normalized?.walkingAidLabel ?? 'Sem informação';
  const physicalAssistance = Boolean(bestAttempt?.physicalAssistance ?? normalized?.physicalAssistance);
  const oxygenUsed = Boolean(bestAttempt?.oxygenUsed ?? normalized?.oxygenUsed);
  const comparableToReference = isGaitSpeedComparableToReference(bestAttempt ?? normalized);
  const referenceComparison =
    normalized?.referenceComparison ?? calculateGaitSpeedReferenceComparison(bestSpeedMs, normalized?.patientContext);
  const clinicalFlags = normalized?.clinicalFlags ?? getGaitSpeedClinicalFlags(bestSpeedMs, completed, physicalAssistance, referenceComparison);
  const severity = getGaitSpeedSeverityLabel(bestSpeedMs, completed, physicalAssistance, referenceComparison);
  const severityRange = getGaitSpeedSeverityRangeLabel(bestSpeedMs, completed, physicalAssistance, referenceComparison);
  const interpretation = resolveAssessmentInterpretationText(
    normalized,
    getGaitSpeedInterpretationText({
      completed,
      timeSeconds: bestTimeSeconds,
      speedMs: bestSpeedMs,
      walkingAidLabel,
      physicalAssistance,
      oxygenUsed,
      reasonNotCompleted: bestAttempt?.reasonNotCompleted ?? normalized?.reasonNotCompleted,
      attemptCount,
      referenceComparison,
    }),
  );

  return {
    attemptCount,
    bestAttempt: best.index,
    bestAttemptPayload: bestAttempt,
    bestTimeSeconds,
    bestSpeedMs,
    completed,
    walkingAidLabel,
    physicalAssistance,
    oxygenUsed,
    comparableToReference,
    referenceComparison,
    referenceStatus: getGaitSpeedReferenceComparisonLabel(referenceComparison),
    clinicalFlags,
    severity,
    severityRange,
    interpretation,
    referenceNote: normalized?.referenceNote ?? GAIT_SPEED_REFERENCE_NOTE,
  };
};

export const getGaitSpeedBarProgress = (speedMs?: number | null): number | null => {
  if (typeof speedMs !== 'number' || Number.isNaN(speedMs)) return null;
  return Math.max(0, Math.min(GAIT_SPEED_BAR_MAX_MS, speedMs)) / GAIT_SPEED_BAR_MAX_MS;
};
